import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, X, Volume2 } from "lucide-react";
import { usePlayer } from "../contexts";
import { resolveFileUrl } from "../api";

export default function MediaPlayer() {
  const { current, isPlaying, setIsPlaying, next, prev, stop, toggle } = usePlayer();
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (current) {
      a.src = resolveFileUrl(current.url);
      a.load();
      if (isPlaying) a.play().catch(() => setIsPlaying(false));
    }
  }, [current?.url]); // eslint-disable-line

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) a.play().catch(() => setIsPlaying(false));
    else a.pause();
  }, [isPlaying]); // eslint-disable-line

  useEffect(() => {
    const a = audioRef.current;
    if (a) a.volume = volume;
  }, [volume]);

  if (!current) return null;

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="global-media-player"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-neutral-800 backdrop-blur-xl"
      style={{ background: "rgba(5,5,5,0.92)" }}
    >
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onEnded={next}
      />
      <input
        type="range"
        className="player-progress block w-full"
        min={0}
        max={duration || 0}
        step={0.1}
        value={progress}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (audioRef.current) audioRef.current.currentTime = v;
          setProgress(v);
        }}
        data-testid="player-seekbar"
      />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        {/* Track meta */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {current.cover ? (
            <img
              src={resolveFileUrl(current.cover)}
              alt=""
              className="w-12 h-12 object-cover border border-neutral-800"
            />
          ) : (
            <div className="w-12 h-12 tape-stripe border border-neutral-800" />
          )}
          <div className="min-w-0">
            <div className="font-headline text-sm uppercase truncate" data-testid="player-track-title">
              {current.title}
            </div>
            <div className="font-mono-label text-[10px] text-neutral-400 truncate">
              {current.artist}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={prev}
            className="text-neutral-300 hover:text-[var(--accent)] transition-colors"
            data-testid="player-prev"
            aria-label="Previous"
          >
            <SkipBack size={20} />
          </button>
          <button
            onClick={toggle}
            className="w-11 h-11 bg-[var(--accent)] hover:bg-[var(--accent-hover)] flex items-center justify-center"
            data-testid="player-toggle"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <button
            onClick={next}
            className="text-neutral-300 hover:text-[var(--accent)] transition-colors"
            data-testid="player-next"
            aria-label="Next"
          >
            <SkipForward size={20} />
          </button>
        </div>

        {/* Time + volume */}
        <div className="hidden md:flex items-center gap-4 font-mono-label text-[10px] text-neutral-400">
          <span>{fmt(progress)} / {fmt(duration)}</span>
          <div className="flex items-center gap-2">
            <Volume2 size={14} />
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20 player-progress"
              data-testid="player-volume"
            />
          </div>
        </div>

        <button
          onClick={stop}
          className="text-neutral-500 hover:text-white transition-colors"
          data-testid="player-close"
          aria-label="Close player"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
